import './zone-traffic-cell.scss';
import './rules-viewer.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { Rule } from '../../../../dtos/version.dto';
import { useStores } from '../../../store';
import { firewallFiltersConfig } from '../../../shared/filters/filter-config/firewall-filters';
import { rulesColumns } from '../firewall-rules-columns-config';
import CustomButton from '../../../shared/custom-button/custom-button';
import { InteractiveDataTable } from '../../../shared/interactive-data-table/interactive-data-table';
import { useNavigate } from 'src/utils/use-navigate';
import { observer } from 'mobx-react-lite';
import CustomIcon, { IconType } from '../../../shared/custom-icon/custom-icon';
import { TableAction } from '../../../shared/table-row/table-row';
import ruleService from '../../../../services/rule.service';
import useCustomModal from '../../../shared/custom-modal/custom-modal';
import usePromiseFeedback from '../../../shared/promise-feedback/usePromiseFeedback';
import { isServiceRule, MAX_CUSTOM_RULE_COUNT } from '../../../../utils/ruleUtils';
import { TooltipDisableReason } from 'src/components/shared/tooltip-disable-message/tooltip-disable-reason';
import { RuleHelper, RuleNameAlias } from '../../../../utils/rule-helper';
import { DeactivateVersion } from '../../../shared/deactivate-version/deactivate-version';
import ChevronDown from '@svg/chevron-down_20.svg?react';

export const RulesViewer: React.FC = observer(() => {
    const { fwVersionStore, userStore, customerStore } = useStores();
    const navigate = useNavigate();
    const [isAddRuleDisabled, setIsAddRuleDisabled] = useState(false);
    const ADD_DISABLED_TOOLTIP_TEXT = 'Max limit of 25 custom rules is reached';
    const [showModal, , contextHolder] = useCustomModal();
    const [promiseFeedbackFactory, promiseContextHolder] = usePromiseFeedback();

    const originalRules: Rule[] = useMemo(() => {
        if (fwVersionStore.selectedFwVersion) {
            const rules = fwVersionStore.selectedFwVersion.content.policy.rules;
            rules.forEach((rule, index) => rule.position ??= (index + 1));
            setIsAddRuleDisabled(fwVersionStore.customRulesCount >= MAX_CUSTOM_RULE_COUNT);
            return rules;
        }
        return [];
    }, [fwVersionStore.selectedFwVersion?.content.policy.rules]);

    useEffect(() => {
        fwVersionStore.setFirewallRulesMode('view');
    }, []);

    const isDisabledEditRule = (ruleName: string) => {
        return [RuleHelper.getRuleName(RuleNameAlias.DROP_ALL),
            RuleHelper.getRuleName(RuleNameAlias.TRUST_TO_UNTRUST_BLOCKED_APPS),
            RuleHelper.getRuleName(RuleNameAlias.UNTRUST_TO_TRUST_BLOCKED_APPS)].includes(ruleName);
    };

    const actions: TableAction<Rule>[] = [
        {
            icon: IconType.PENCIL,
            label: () => 'Edit',
            disabled: (rowData) => !fwVersionStore.isSelectedVersionEditable || isDisabledEditRule(rowData.displayName),
            handler: rowData => {
                navigate(`edit/${rowData.id}`);
            }
        },
        {
            icon: IconType.CLEAR,
            label: (rowData) => rowData.disabled ? 'Activate' : 'Deactivate',
            disabled: (rowData) => rowData.displayName === RuleHelper.getRuleName(RuleNameAlias.DROP_ALL) || !fwVersionStore.isSelectedVersionEditable
                || (isServiceRule(rowData.displayName) && !!rowData.disabled),
            tooltip: 'Drop All Rule cannot be deactivated',
            handler: rowData => {
                const activeState = rowData.disabled ? 'active' : 'inactive';
                const deactivateText = 'By Deactivating this rule, its restrictions will not be ' +
                    'enforced once the draft policy version is applied. You can always reactivate this rule again';
                if (!rowData.disabled) {
                    showModal(
                        {
                            width: 480,
                            title: 'Deactivate Rule',
                            body: () => <div className="modal-deactivation-commit-message">{
                                deactivateText}</div>,
                            okText: 'Deactivate',
                            cancelText: 'Cancel',
                            onOk: () => promiseFeedbackFactory({
                                promise: fwVersionStore.genericCrudMethod(ruleService.deactivateRule, [rowData, activeState])
                            })
                        });
                } else {
                    promiseFeedbackFactory({
                        promise: fwVersionStore.genericCrudMethod(ruleService.deactivateRule, [rowData, activeState])
                    });
                }
            }
        },
        {
            icon: IconType.NEW,
            label: () => 'Add New Rule',
            disabled: () => isAddRuleDisabled || !fwVersionStore.isSelectedVersionEditable,
            tooltip: isAddRuleDisabled ? ADD_DISABLED_TOOLTIP_TEXT : '',
            handler: rowData => {
                fwVersionStore.calculateNewRulePosition(rowData.id);
                navigate('add');
            }
        },
        {
            icon: IconType.TRASH,
            label: () => 'Delete',
            disabled: (rule) => rule.isDefault || !fwVersionStore.isSelectedVersionEditable,
            tooltip: 'Default Rules cannot be deleted',
            handler: rowData => {
                showModal(
                    {
                        width: 480,
                        title: `Delete Rule?`,
                        body: () => <div className="modal-deactivation-commit-message">{
                            `By deleting this rule, its restrictions will no longer be\nenforced once draft policy version is applied.`}</div>,
                        okText: 'Yes, delete it',
                        cancelText: 'Cancel',
                        wrapClassName: 'delete-rule-modal',
                        onOk: () => promiseFeedbackFactory({
                            promise: fwVersionStore.genericCrudMethod(ruleService.deleteRule, [rowData])
                        })
                    });
            }
        }
    ];

    const addNewRule = () => {
        fwVersionStore.calculateNewRulePosition();
        navigate('add');
    };

    return <>
        {contextHolder}
        <InteractiveDataTable
            filterConfig={firewallFiltersConfig}
            data={originalRules}
            columnsConfig={rulesColumns}
            isInactiveRow={(row: Rule) => !!row.disabled}
            actions={actions}
            dataHasLoaded={!!fwVersionStore.selectedFwVersion}
            isLoading={fwVersionStore.isLoading || !fwVersionStore.selectedFwVersion}
        >
            <>
                <TooltipDisableReason overlayClassName="add-rule-tooltip"
                                      disableReason={isAddRuleDisabled ? ADD_DISABLED_TOOLTIP_TEXT : fwVersionStore.getDisableReason}>
                    <div style={{ display: 'inline-block' }}>
                        <CustomButton label={'Add Rule'}
                                      prefixIcon={<CustomIcon type={IconType.PLUS} />}
                                      className={'custom-btn secondary-btn'}
                                      isDisabled={isAddRuleDisabled || !fwVersionStore.isSelectedVersionEditable}
                                      onClick={() => addNewRule()} />
                    </div>
                </TooltipDisableReason>
                {!userStore.isReadOnlyUser && customerStore.isAvailable && !fwVersionStore.pendingVersion &&
                    <DeactivateVersion protectionType={'DYNAMIC_DEFENSE'} popoverIcon={<ChevronDown />}/>}
            </>
        </InteractiveDataTable>
        {promiseContextHolder}
    </>;
});